import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Loader2, Compass, Sparkles, MapPin, Lock, X, Pencil } from "lucide-react";
import { API_BASE } from "@/lib/api";
import { JourneyPlanView } from "@/components/site/JourneyPlan";

const INTERESTS = [
  "Yoga", "Running", "Coffee", "Adventure", "Ayurveda", "Recovery", "Cycling", "Nature",
  "Meditation", "Healthy Food", "Digital Detox", "Strength Training", "Spa", "Wildlife", "Photography", "Biohacking",
];
const INTEREST_DESTS = {
  Yoga: ["Rishikesh", "Mysore", "Kerala"], Running: ["Ladakh", "Goa"], Coffee: ["Coorg", "Chikmagalur", "Bangalore"],
  Adventure: ["Ladakh", "Rishikesh"], Ayurveda: ["Kerala"], Recovery: ["Kerala", "Dharamshala"],
  Cycling: ["Ladakh", "Coorg"], Nature: ["Coorg", "Sikkim", "Andaman"], Meditation: ["Rishikesh", "Dharamshala"],
  "Healthy Food": ["Goa", "Bangalore"], "Digital Detox": ["Sikkim", "Coorg"], "Strength Training": ["Goa", "Bangalore"],
  Spa: ["Kerala", "Udaipur"], Wildlife: ["Ranthambore", "Kabini"], Photography: ["Ladakh", "Jaipur"], Biohacking: ["Bangalore", "Goa"],
};

const STEPS = [
  { id: "origin", q: "Where are you travelling from?", sub: "This shapes flights, budget currency and acclimatisation.", options: ["India", "United Kingdom", "United States", "UAE", "Singapore", "Australia", "Germany", "Canada"], allowCustom: true },
  { id: "days", q: "How many days do you have?", sub: "We'll pace the journey around this.", options: ["3", "5", "7", "10", "12", "14", "21", "28"], labels: { 3: "Weekend · 2–3 days", 5: "5 days", 7: "7 days", 10: "10 days", 12: "12 days", 14: "14 days", 21: "21 days · Transformation", 28: "28 days · Ultimate" } },
  { id: "travel_month", q: "When are you travelling?", sub: "India changes with the seasons — harvests, festivals, marathons. We'll plan around them.", options: ["Flexible", "January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"] },
  { id: "party", q: "Who's coming along?", sub: "Journeys feel different solo vs. together.", options: ["Solo", "Couple", "Family", "Group of friends"] },
  { id: "age_group", q: "Your age group?", sub: "Helps us calibrate intensity and style.", options: ["18–24", "25–34", "35–44", "45–54", "55+"] },
  { id: "travel_style", q: "How do you like to travel?", sub: "Be honest — there's no wrong answer.", options: ["Luxury", "Comfort", "Budget"] },
  { id: "budget", q: "Roughly, your total budget?", sub: "Excluding international flights.", options: ["Under $800 (~₹70k)", "$800–1,500 (~₹70k–1.3L)", "$1,500–3,000 (~₹1.3L–2.6L)", "$3,000–5,000 (~₹2.6L–4.3L)", "$5,000+ (~₹4.3L+)"] },
  { id: "fitness_level", q: "How active are you right now?", sub: "So training and treks land at the right intensity.", options: ["Just starting", "Active", "Very fit", "Athlete"] },
  { id: "india_experience", q: "Have you explored India before?", sub: "First-timers get gentler landings.", options: ["First time", "Been before", "I live in India"] },
  { id: "interests", q: "What excites you?", sub: "Pick everything that sparks something — this is the soul of your journey.", multi: true },
];

const LOADING_LINES = [
  "Reading your travel style…",
  "Mapping destinations to your interests…",
  "Balancing training days with recovery…",
  "Finding coffee estates along your route…",
  "Choosing stays that match your budget…",
  "Weaving it all into one journey…",
];

export default function JourneyBuilder() {
  const location = useLocation();
  const preset = location.state || {};
  const [phase, setPhase] = useState("quiz");
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({ interests: (preset.interests || []).filter((i) => INTERESTS.includes(i)) });
  const [preferredDest, setPreferredDest] = useState(preset.destination || null);
  const [custom, setCustom] = useState("");
  const [loadLine, setLoadLine] = useState(0);
  const [result, setResult] = useState(null);
  const [lead, setLead] = useState({ name: "", email: "" });
  const [unlocking, setUnlocking] = useState(false);

  useEffect(() => {
    if (phase !== "loading") return;
    const t = setInterval(() => setLoadLine((l) => (l + 1) % LOADING_LINES.length), 2800);
    return () => clearInterval(t);
  }, [phase]);

  const cur = STEPS[step];
  const hints = useMemo(() => {
    const set = new Set();
    (answers.interests || []).forEach((i) => (INTEREST_DESTS[i] || []).forEach((d) => set.add(d)));
    return [...set].slice(0, 6);
  }, [answers.interests]);

  const pick = (val) => {
    setAnswers((a) => ({ ...a, [cur.id]: val }));
    setCustom("");
    if (step < STEPS.length - 1) setStep(step + 1);
  };
  const toggleInterest = (i) =>
    setAnswers((a) => ({ ...a, interests: a.interests.includes(i) ? a.interests.filter((x) => x !== i) : [...a.interests, i] }));

  const generate = async () => {
    setPhase("loading");
    try {
      const r = await axios.post(`${API_BASE}/journey/generate`, { ...answers, days: parseInt(answers.days, 10), preferred_destination: preferredDest }, { withCredentials: true, timeout: 180000 });
      setResult(r.data);
      setPhase(r.data.unlocked ? "plan" : "teaser");
      window.scrollTo({ top: 0 });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not craft your journey — please try again.");
      setPhase("quiz");
    }
  };

  const unlock = async (e) => {
    e.preventDefault();
    setUnlocking(true);
    try {
      const r = await axios.post(`${API_BASE}/journey/${result.id}/unlock`, lead);
      setResult(r.data);
      setPhase("plan");
      window.scrollTo({ top: 0 });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not unlock — check your email address.");
    } finally {
      setUnlocking(false);
    }
  };

  if (phase === "plan" && result?.plan) {
    return (
      <div className="min-h-screen bg-[#F7F5F0] text-[#2D372B] px-6 md:px-12 py-14 md:py-20">
        <div className="max-w-4xl mx-auto mb-8 flex items-center justify-between print:hidden">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#5C665A] hover:text-[#2D372B]"><ArrowLeft size={15} /> Back to Coach Sanjay</Link>
          <button data-testid="journey-restart-btn" onClick={() => { setPhase("quiz"); setStep(0); setAnswers({ interests: [] }); setResult(null); }} className="text-sm text-[#89937C] hover:text-[#2D372B] inline-flex items-center gap-1.5"><Compass size={14} /> Plan another journey</button>
        </div>
        <JourneyPlanView plan={result.plan} journeyId={result.id} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#232B21] text-[#F7F5F0]">
      <style>{`@keyframes jbUp{from{opacity:0;transform:translateY(14px)}to{opacity:1;transform:translateY(0)}} .jb-up{animation:jbUp .45s ease both}`}</style>
      <div className="max-w-6xl mx-auto px-6 md:px-12 py-10 md:py-16 pb-28 md:pb-16">
        <div className="flex items-center justify-between mb-10">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#A9B39D] hover:text-[#F7F5F0] transition-colors"><ArrowLeft size={15} /> Coach Sanjay</Link>
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#A9B39D]"><Sparkles size={13} className="text-[#C57B57]" /> AI Journey Builder</div>
        </div>

        {phase === "loading" && (
          <div className="min-h-[60vh] flex flex-col items-center justify-center text-center jb-up" data-testid="journey-loading">
            <div className="relative w-20 h-20 mb-8">
              <div className="absolute inset-0 rounded-full border-2 border-[#89937C]/30" />
              <div className="absolute inset-0 rounded-full border-t-2 border-[#C57B57] animate-spin" />
              <Compass size={28} className="absolute inset-0 m-auto text-[#A9B39D]" />
            </div>
            <h2 className="font-serif-display text-3xl sm:text-4xl">Crafting your journey…</h2>
            <p key={loadLine} className="mt-4 text-[#A9B39D] jb-up">{LOADING_LINES[loadLine]}</p>
            <p className="mt-10 text-xs text-[#5C665A]">This usually takes 20–40 seconds. Worth the wait.</p>
          </div>
        )}

        {phase === "teaser" && result?.teaser && (
          <div className="max-w-xl mx-auto min-h-[60vh] flex flex-col justify-center jb-up" data-testid="journey-teaser">
            <div className="text-xs uppercase tracking-[0.25em] text-[#C57B57] mb-3">Your journey is ready</div>
            <h1 className="font-serif-display text-4xl sm:text-5xl leading-[1.05]">{result.teaser.title}</h1>
            <p className="mt-3 italic text-[#C57B57] font-serif-display text-lg">{result.teaser.tagline}</p>
            <p className="mt-5 text-[#C9CFC0] leading-relaxed">{result.teaser.summary}</p>
            <div className="mt-5 flex flex-wrap gap-2">
              {(result.teaser.destinations || []).map((d) => (
                <span key={d.name} className="inline-flex items-center gap-1.5 rounded-full border border-[#89937C]/50 px-3.5 py-1.5 text-sm text-[#C9CFC0]"><MapPin size={12} className="text-[#89937C]" /> {d.name}</span>
              ))}
              <span className="inline-flex items-center rounded-full border border-[#89937C]/50 px-3.5 py-1.5 text-sm text-[#C9CFC0]">{result.teaser.days_count} days</span>
            </div>
            <form onSubmit={unlock} className="mt-9 bg-white/5 border border-white/10 rounded-3xl p-7 backdrop-blur-xl">
              <div className="flex items-center gap-2 text-sm text-[#A9B39D] mb-4"><Lock size={14} className="text-[#C57B57]" /> Unlock your full day-by-day itinerary — free</div>
              <input data-testid="journey-name-input" required minLength={2} value={lead.name} onChange={(e) => setLead({ ...lead, name: e.target.value })} placeholder="Your name" className="w-full h-12 rounded-xl bg-[#232B21] border border-white/15 px-4 text-[#F7F5F0] placeholder:text-[#5C665A] mb-3 focus:outline-none focus:border-[#89937C]" />
              <input data-testid="journey-email-input" required type="email" value={lead.email} onChange={(e) => setLead({ ...lead, email: e.target.value })} placeholder="Email address" className="w-full h-12 rounded-xl bg-[#232B21] border border-white/15 px-4 text-[#F7F5F0] placeholder:text-[#5C665A] mb-4 focus:outline-none focus:border-[#89937C]" />
              <button data-testid="journey-unlock-btn" disabled={unlocking} className="w-full inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 bg-[#C57B57] text-white font-medium hover:bg-[#AD6A48] transition-colors disabled:opacity-60">
                {unlocking ? <Loader2 size={15} className="animate-spin" /> : <Compass size={15} />} Reveal my full itinerary
              </button>
            </form>
          </div>
        )}

        {phase === "quiz" && (
          <div className="grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-7">
              <div className="flex gap-1.5 mb-10">
                {STEPS.map((s, i) => (
                  <div key={s.id} className={`h-1 flex-1 rounded-full transition-colors duration-500 ${i <= step ? "bg-[#C57B57]" : "bg-white/10"}`} />
                ))}
              </div>
              <div key={step} className="jb-up">
                {preferredDest && (
                  <button
                    data-testid="preferred-dest-chip"
                    onClick={() => setPreferredDest(null)}
                    title="Remove anchor destination"
                    className="mb-5 inline-flex items-center gap-2 rounded-full border border-[#C57B57]/50 bg-[#C57B57]/15 px-4 py-2 text-sm text-[#E8C9B8] hover:border-[#C57B57] transition-colors"
                  >
                    <MapPin size={13} /> Journey anchored to <span className="font-semibold text-white">{preferredDest}</span>
                    <X size={13} className="opacity-70" />
                  </button>
                )}
                <div className="text-xs uppercase tracking-[0.25em] text-[#89937C] mb-3">Question {step + 1} of {STEPS.length}</div>
                <h1 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl leading-[1.08]">{cur.q}</h1>
                <p className="mt-3 text-[#A9B39D]">{cur.sub}</p>

                {cur.multi ? (
                  <>
                    <div className="mt-8 flex flex-wrap gap-2.5">
                      {INTERESTS.map((i) => (
                        <button key={i} type="button" data-testid={`interest-${i.toLowerCase().replace(/ /g, "-")}`} onClick={() => toggleInterest(i)}
                          className={`rounded-full px-5 py-2.5 text-sm border transition-all duration-200 ${answers.interests.includes(i) ? "bg-[#C57B57] border-[#C57B57] text-white" : "border-white/20 text-[#C9CFC0] hover:border-[#C57B57]/60"}`}>
                          {i}
                        </button>
                      ))}
                    </div>
                    <button data-testid="journey-generate-btn" onClick={generate} disabled={answers.interests.length === 0}
                      className="mt-10 inline-flex items-center gap-2 rounded-full px-9 py-4 bg-[#C57B57] text-white font-medium hover:bg-[#AD6A48] transition-colors disabled:opacity-40">
                      <Sparkles size={16} /> Craft my journey
                    </button>
                    {Object.keys(answers).some((k) => k !== "interests" && answers[k]) && (
                      <div className="mt-8 lg:hidden">
                        <div className="text-xs uppercase tracking-[0.25em] text-[#89937C] mb-3 inline-flex items-center gap-1.5"><Pencil size={11} /> Tap any answer to change it</div>
                        <div className="flex flex-wrap gap-2">
                          {STEPS.filter((s) => !s.multi && answers[s.id]).map((s) => (
                            <button key={s.id} data-testid={`edit-answer-${s.id}`} onClick={() => setStep(STEPS.indexOf(s))}
                              className="rounded-full bg-[#89937C]/15 border border-[#89937C]/30 px-3.5 py-1.5 text-xs text-[#C9CFC0] hover:border-[#C57B57] transition-colors">
                              {s.labels?.[answers[s.id]] || answers[s.id]}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="mt-8 flex flex-wrap gap-2.5">
                    {cur.options.map((o) => (
                      <button key={o} type="button" data-testid={`journey-opt-${cur.id.replace(/_/g, "-")}-${String(o).toLowerCase().replace(/\+/g, "plus").replace(/[^a-z0-9]+/g, "-")}`} onClick={() => pick(o)}
                        className={`rounded-full px-5 py-3 text-sm border transition-all duration-200 ${answers[cur.id] === o ? "bg-[#89937C] border-[#89937C] text-white" : "border-white/20 text-[#C9CFC0] hover:border-[#89937C] hover:text-white"}`}>
                        {cur.labels?.[o] || o}
                      </button>
                    ))}
                    {cur.allowCustom && (
                      <form onSubmit={(e) => { e.preventDefault(); if (custom.trim().length >= 2) pick(custom.trim()); }} className="flex gap-2 w-full mt-2">
                        <input data-testid="journey-origin-custom" value={custom} onChange={(e) => setCustom(e.target.value)} placeholder="Somewhere else? Type your country…" className="flex-1 h-12 rounded-xl bg-white/5 border border-white/15 px-4 text-[#F7F5F0] placeholder:text-[#5C665A] focus:outline-none focus:border-[#89937C]" />
                        <button className="rounded-full px-5 border border-white/20 text-[#C9CFC0] hover:border-[#89937C] transition-colors"><ArrowRight size={16} /></button>
                      </form>
                    )}
                  </div>
                )}

                {step > 0 && (
                  <button data-testid="journey-back-btn" onClick={() => setStep(step - 1)} className="mt-8 inline-flex items-center gap-1.5 text-sm text-[#A9B39D] hover:text-[#F7F5F0] transition-colors">
                    <ArrowLeft size={14} /> Back
                  </button>
                )}
              </div>
            </div>

            <aside className="hidden lg:block lg:col-span-5">
              <div className="sticky top-10 bg-white/5 border border-white/10 rounded-3xl p-8 backdrop-blur-xl">
                <div className="flex items-center justify-between mb-5">
                  <div className="text-xs uppercase tracking-[0.25em] text-[#89937C]">Your journey so far</div>
                  <div className="text-[10px] uppercase tracking-wider text-[#5C665A] inline-flex items-center gap-1"><Pencil size={10} /> tap to edit</div>
                </div>
                {Object.keys(answers).filter((k) => k !== "interests" && answers[k]).length === 0 && answers.interests.length === 0 && !preferredDest ? (
                  <p className="text-sm text-[#5C665A]">Answer a few questions and watch your journey take shape here…</p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {preferredDest && (
                      <button onClick={() => setPreferredDest(null)} title="Remove" className="rounded-full bg-[#C57B57]/20 border border-[#C57B57]/40 px-3.5 py-1.5 text-xs text-[#E8C9B8] inline-flex items-center gap-1.5 hover:border-[#C57B57] transition-colors">
                        <MapPin size={11} /> {preferredDest} <X size={11} className="opacity-70" />
                      </button>
                    )}
                    {STEPS.filter((s) => !s.multi && answers[s.id]).map((s) => (
                      <button key={s.id} data-testid={`panel-answer-${s.id}`} onClick={() => setStep(STEPS.indexOf(s))}
                        className="rounded-full bg-[#89937C]/15 border border-[#89937C]/30 px-3.5 py-1.5 text-xs text-[#C9CFC0] hover:border-[#C57B57] hover:text-white transition-colors">
                        {s.labels?.[answers[s.id]] || answers[s.id]}
                      </button>
                    ))}
                    {answers.interests.map((i) => (
                      <button key={i} onClick={() => setStep(STEPS.length - 1)}
                        className="rounded-full bg-[#C57B57]/15 border border-[#C57B57]/30 px-3.5 py-1.5 text-xs text-[#E8C9B8] hover:border-[#C57B57] transition-colors">
                        {i}
                      </button>
                    ))}
                  </div>
                )}
                {hints.length > 0 && (
                  <div className="mt-7 pt-6 border-t border-white/10" data-testid="journey-hints">
                    <div className="text-xs uppercase tracking-[0.25em] text-[#C57B57] mb-3">India is whispering…</div>
                    <div className="flex flex-wrap gap-2">
                      {hints.map((d) => (
                        <span key={d} className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-3.5 py-1.5 text-sm text-[#F7F5F0]"><MapPin size={12} className="text-[#89937C]" /> {d}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </aside>
          </div>
        )}
      </div>
    </div>
  );
}
